import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Modal from "../../common/Modal";
import { useChats } from "../../../hooks/useChats";
import Autocomplete from "../../common/Autocomplete";
import api from "../../../services/api";
import { useAuth } from "../../../hooks/useAuth";
import { Tooltip } from "react-tooltip";
import { useDebouncedCallback } from "use-debounce";
import { normalizList, sortByCode, hasModifier, getCpt2GroupHeader, reorderList, stampNonBillable } from "../../../utils/utils";
import { mergeIcd10SearchOptions } from "../../../utils/icd10Library";
import { normalizeSex } from "../../../utils/icd10SexGate";
import { classifyCodeForSex } from "../../../utils/icd10SexEdits";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import ConditionalDndProvider from "../../common/ConditionalDndProvider";
import { useAnimatedDragList, DraggableRow } from "../../../hooks/useAnimatedDragList";
import { useRipple } from "../../../hooks/useRipple";
import Icd10CodePicker from "../../common/Icd10CodePicker";
import {
  CPT_MODIFIER_OPTIONS,
  MAX_UNITS,
  clampUnits,
  displayUnits,
  hasExplicitUnits,
  isBillingRulesActive,
  lineModifier,
  lineUnits,
  withCptModifier,
  withUnits,
} from "../../../utils/billingRules";
import { isNonBillableList } from "../../../utils/notBilled";

// No Edit Codes row carries a billing control. Regular CPT lists (Office Procedures, CPT Level II)
// dropped theirs on 2026-10-22; the Non-Billable Injection section dropped its reason select on
// 2026-10-27, since every row there is unbilled by construction (stampNonBillable on save).

// patientSex: the patient's recorded sex (chat.user.custom.gender, any casing
// normalizeSex accepts). Only the note's Edit Codes mount passes it; the DME
// order and table-view mounts leave it undefined, which disables the pre-check.
function ModalICD10AddCode({ section, close, restoreScroll, onSave, initialCodes = [], type, patientSex }) {
  const { t } = useTranslation();
  const [options, setOptions] = useState([]);
  const { updateContext, chat } = useChats();
  const { auth, setAuth } = useAuth();
  const [codes, setCodes] = useState(() => {
    const list = normalizList(initialCodes);
    if (type === "cpt2codes") list.sort(sortByCode);
    return list;
  });
  // Decided once from what the modal opened with: the backend's findings variable or
  // any line already carrying cptModifier/units. Off = today's controls, unchanged.
  const [billingRulesActive] = useState(() => isBillingRulesActive(chat?.contextVariables, initialCodes));
  const nonBillable = isNonBillableList(type);
  const isCptPicker = type === "cpt2codes" || type === "inOfficeProcedures" || nonBillable;
  const unitsSelectValue = (item) => {
    if (nonBillable) return displayUnits(item);
    if (!billingRulesActive) return item.modifier || 1;
    if (!hasExplicitUnits(type, item)) return "";
    return lineUnits(item) ?? item.modifier;
  };
  const modifierOptionsFor = (item) => {
    const current = lineModifier(item);
    return current && !CPT_MODIFIER_OPTIONS.includes(current) ? [...CPT_MODIFIER_OPTIONS, current] : CPT_MODIFIER_OPTIONS;
  };
  const [showMove, setShowMove] = useState(false);
  const [newGroup, setNewGroup] = useState();
  const [newGroupName, setNewGroupName] = useState();
  const [favourites, setFavourites] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(null);
  const highlightTimer = useRef(null);

  const isDraggable = type !== "cpt2codes";
  const listContainerRef = useRef(null);

  const { capturePositions } = useAnimatedDragList(codes, listContainerRef);

  const moveItem = useCallback(
    (fromIndex, toIndex) => {
      setCodes((prev) => reorderList(prev, fromIndex, toIndex));
    },
    [setCodes]
  );

  const createRipple = useRipple();

  const moveToTop = (i) => {
    if (i <= 0 || i >= codes.length) return;
    capturePositions();
    setCodes((prev) => reorderList(prev, i, 0));
    setHighlightedIndex(0);
    clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(() => setHighlightedIndex(null), 800);
  };

  useEffect(() => {
    return () => clearTimeout(highlightTimer.current);
  }, []);

  const favKey =
    type === "cpt2codes"
      ? "favoriteCPT2Codes"
      : type === "inOfficeProcedures" || nonBillable
        ? "favoriteCPTCodes"
        : "icd10favourites";

  const listCodes = useMemo(() => {
    let list = [];

    for (let group of Object.keys(favourites || {})) {
      list = list.concat(favourites[group].map((item) => item.code));
    }

    return list;
  }, [favourites]);

  useEffect(() => {
    let favs = {};

    for (let group of Object.keys(auth.user.custom?.[favKey] || {})) {
      favs[group] = normalizList(auth.user.custom[favKey][group]);
      favs[group] = favs[group].sort((a, b) => {
        if (typeof a === "string") {
          a = a.split("-")?.[1];
        } else {
          a = a.description;
        }

        if (typeof b === "string") {
          b = b.split("-")?.[1];
        } else {
          b = b.description;
        }

        if (a > b) {
          return 1;
        }
        return 0;
      });
    }

    setFavourites(favs);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCodes = async (search) => {
    try {
      let apiMethod = api.getIcd10;
      if (type === "inOfficeProcedures") {
        apiMethod = api.getCptCodes;
      } else if (type === "cpt2codes") {
        apiMethod = api.getCpt2Codes;
      }
      let resultList;
      if (nonBillable) {
        // cpt-1 has no J/Q drug codes, so the drug row can only be found in hcpcs-2.
        // allSettled: one failing endpoint must not blank the other's results.
        const settled = await Promise.allSettled([api.getCptCodes(search), api.getCpt2Codes(search)]);
        const [cpt1, hcpcs] = settled.map((r) => (r.status === "fulfilled" ? r.value : null));
        settled.forEach((r) => r.status === "rejected" && console.error("Failed to fetch codes", r.reason));
        const seen = new Set();
        resultList = [...(cpt1?.data?.result || []), ...(hcpcs?.data?.result || [])].filter((item) => {
          if (!item || seen.has(item.n)) return false;
          seen.add(item.n);
          return true;
        });
      } else {
        let res = await apiMethod(search);
        resultList = res.data.result;
      }
      // Only the ICD-10 billable-codes picker gets the org custom-library merge.
      if (type === "billableCodesICD10") {
        resultList = mergeIcd10SearchOptions(resultList, auth.orgInfo?.icd10Library, search);
      }

      let options = [];
      let sortedList = resultList.sort(sortCodes);
      for (let item of sortedList) {
        const value = item.n + " - " + item.d;
        options.push({
          key: value,
          label: item.custom ? value + "  •  " + t("modals.icd10AddCode.orgTag", "Org") : value,
          value: item,
        });
      }
      setOptions(options);
    } catch (e) {
      console.error("Failed to fetch codes", e);
    }
  };

  const addCode = (c) => {
    if (!c) return;

    if (codes.find((item) => item.code === c.n)) {
      toast.warn(t("modals.icd10AddCode.codeAlreadyPresent"));
      return;
    }
    // Sex pre-check. applyIcd10SexGate (refreshChat) silently strips a code that
    // is anatomically impossible for the patient's recorded sex on the very next
    // refetch, so adding one here only produced a "Code added" toast for a code
    // that was about to vanish from the note. Refuse at add time and say why.
    // Fails open exactly like the gate: no recorded sex means no check.
    const sex = normalizeSex(patientSex);
    if (sex) {
      const { conflict, mirror } = classifyCodeForSex(c.n, sex);
      if (conflict) {
        const codeSex = t(sex === "F" ? "modals.icd10AddCode.sexMale" : "modals.icd10AddCode.sexFemale");
        const recordedSex = t(sex === "F" ? "modals.icd10AddCode.sexFemale" : "modals.icd10AddCode.sexMale");
        toast.warn(
          t(mirror ? "modals.icd10AddCode.sexConflictMirror" : "modals.icd10AddCode.sexConflict", {
            code: c.n,
            codeSex,
            recordedSex,
            mirrorCode: mirror?.code,
            mirrorDescription: mirror?.description,
          }),
          { toastId: "icd10-sex-conflict" }
        );
        return;
      }
    }
    const newCodes = [...codes, { code: c.n, description: c.d }];
    if (type === "cpt2codes") newCodes.sort(sortByCode);
    setCodes(newCodes);
    toast.success(t("modals.icd10AddCode.codeAdded"));
  };

  const save = () => {
    if (onSave) {
      onSave(nonBillable ? stampNonBillable(codes) : codes);
      close();
      return;
    }
    let newList = nonBillable ? stampNonBillable(codes) : normalizList(codes);

    updateContext(type, newList);
    restoreScroll();
    close();
  };

  const removeCode = (index) => {
    const updated = [...codes];
    updated.splice(index, 1);
    setCodes(updated);
  };

  const removeCodeFavourites = (group, index) => {
    const updated = { ...favourites, [group]: [...favourites[group]] };
    updated[group].splice(index, 1);
    setFavourites(updated);

    if (!auth.user.custom[favKey]) {
      auth.user.custom[favKey] = {};
    }
    auth.user.custom[favKey] = updated;
    setAuth({ ...auth });
    api.updateProfile({ custom: { [favKey]: updated } }).catch((e) => console.error("Failed to update profile", e));
  };

  const onTypeCode = (e) => {
    let minChar = 3;
    if (isCptPicker) {
      minChar = 0;
    }
    if (e && e.length > minChar) {
      fetchCodesDebounce(e);
    }
  };

  const fetchCodesDebounce = useDebouncedCallback(fetchCodes, 500);

  const addCodeFavourites = (group, code) => {
    const updated = { ...favourites, [group]: [...(favourites[group] || []), code] };

    setFavourites(updated);

    if (!auth.user.custom[favKey]) {
      auth.user.custom[favKey] = {};
    }
    auth.user.custom[favKey] = updated;
    setAuth({ ...auth });
    api.updateProfile({ custom: { [favKey]: updated } }).catch((e) => console.error("Failed to update profile", e));
  };

  const moveToGroup = () => {
    let group = newGroup === "-1" ? newGroupName : newGroup;
    removeCodeFavourites(showMove.group, showMove.index);
    addCodeFavourites(group, showMove.icd10);
    setShowMove(false);
    setNewGroup("");
    setNewGroupName("");
  };

  const sortCodes = (a, b) => {
    if (a.n < b.n) {
      return -1;
    }
    if (a.n > b.n) {
      return 1;
    }
    return 0;
  };

  return (
    <ConditionalDndProvider>
      <Modal
        show={section}
        close={close}
        size="x-large"
        title={t("modals.icd10AddCode.editCodes")}
        className={"edit-context-variable-modal min-height dx-edit-codes"}
      >
        <div className="dx-search dx-edit-search">
          <i className="notranslate material-icons-outlined dx-search-icon">search</i>
          {/* ICD-10 types get the multi-modal picker engine (codes with/without
              decimal, names, abbreviations like HTN, family grouping). CPT and
              HCPCS keep the flat Autocomplete + fetchCodes path. */}
          {!isCptPicker ? (
            <Icd10CodePicker
              variant="inline"
              resetOnPick
              onPick={(item) => addCode(item)}
              icd10Library={type === "billableCodesICD10" ? auth.orgInfo?.icd10Library : undefined}
              flagNonBillable={type === "billableCodesICD10" ? "hide" : false}
              placeholder={t("modals.icd10AddCode.addCodePlaceholder")}
            />
          ) : (
            <Autocomplete
              onChange={onTypeCode}
              onSelect={(e) => e && addCode(e.value)}
              data={options}
              placeholder={t("modals.icd10AddCode.addCodePlaceholder")}
              resetOnSelect={true}
            />
          )}
        </div>

        <div className="dx-edit-columns">
          <div>
            <h3>{t("modals.icd10AddCode.actuals")}</h3>
            {nonBillable && <p className="icd-10-non-billable-hint">{t("modals.icd10AddCode.nonBillableHint")}</p>}
            {codes.length > 0 ? (
              <div className="icd-10-list" ref={listContainerRef}>
                {codes.map((icd10, index) => {
                  const groupHeader = type === "cpt2codes" || nonBillable ? getCpt2GroupHeader(codes, index, type) : null;
                  const isHighlighted = highlightedIndex === index;
                  const itemId = icd10.code || "code_" + index;

                  const badgeClassName = ["badge", "gray", isDraggable ? "clickable" : "", isHighlighted ? "highlighted" : ""]
                    .filter(Boolean)
                    .join(" ");

                  return (
                    <Fragment key={itemId}>
                      {groupHeader && <div className="icd-10-group-header">{groupHeader}</div>}
                      <DraggableRow
                        itemId={itemId}
                        index={index}
                        onMove={moveItem}
                        onBeforeMove={capturePositions}
                        disabled={!isDraggable}
                        className={"icd-10-item" + (isDraggable ? " draggable" : "")}
                      >
                        {({ dragRef }) => (
                          <>
                            {isDraggable && (
                              <span ref={dragRef} className="material-symbols-outlined drag-handle">
                                drag_indicator
                              </span>
                            )}
                            <div className="icd-10-code">
                              <div className={badgeClassName} onClick={isDraggable ? () => moveToTop(index) : undefined}>
                                {icd10.code}
                              </div>
                            </div>
                            {hasModifier(type, icd10.code) && (
                              /* Units count, 1..MAX_UNITS (oncology drug lines bill in the hundreds).
                                 An empty field is left alone so the row keeps its last value; the
                                 placeholder marks a line the slip needs units for that has none. */
                              <input
                                type="number"
                                inputMode="numeric"
                                min={1}
                                max={MAX_UNITS}
                                step={1}
                                style={{ width: 90 }}
                                className="small icd-10-units-input"
                                aria-label={billingRulesActive ? t("modals.icd10AddCode.units") : undefined}
                                title={t("modals.icd10AddCode.unitsRange", { max: MAX_UNITS })}
                                placeholder={t("modals.icd10AddCode.unitsPlaceholder")}
                                value={unitsSelectValue(icd10)}
                                onChange={(e) => {
                                  const n = clampUnits(e.target.value);
                                  if (n == null) return;
                                  const updated = codes.map((c, idx) =>
                                    idx === index ? withUnits(c, n, { type, active: billingRulesActive }) : c
                                  );
                                  setCodes(updated);
                                }}
                              />
                            )}
                            {billingRulesActive && hasModifier(type, icd10.code) && (
                              <select
                                style={{ width: 90 }}
                                className="small icd-10-cpt-modifier-select"
                                aria-label={t("modals.icd10AddCode.modifier")}
                                value={lineModifier(icd10)}
                                onChange={(e) => {
                                  const updated = codes.map((c, idx) => (idx === index ? withCptModifier(c, e.target.value) : c));
                                  setCodes(updated);
                                }}
                              >
                                <option value="">{t("modals.icd10AddCode.noModifier")}</option>
                                {modifierOptionsFor(icd10).map((m) => (
                                  <option key={m} value={m}>
                                    {m}
                                  </option>
                                ))}
                              </select>
                            )}
                            <div className="icd-10-desc">{icd10.description}</div>
                            <div className="icd-10-action">
                              {listCodes.indexOf(icd10.code) === -1 && (
                                <button className="small white no-border round">
                                  <i
                                    className="notranslate material-icons-outlined"
                                    onClick={() => addCodeFavourites("default", icd10)}
                                  >
                                    star_rate
                                  </i>
                                </button>
                              )}
                              <button className="small white no-border round">
                                <i className="notranslate material-icons" onClick={() => removeCode(index)}>
                                  delete
                                </i>
                              </button>
                            </div>
                          </>
                        )}
                      </DraggableRow>
                    </Fragment>
                  );
                })}
              </div>
            ) : (
              <p className="empty">{t("modals.icd10AddCode.noCodesAdded")}</p>
            )}
            <Tooltip id="my-tooltip-icd" />

            {showMove && (
              <Modal show={showMove} close={() => setShowMove()} size="small" title={t("modals.icd10AddCode.moveToGroup")}>
                <div className="min-height">
                  <div className="row-form">
                    <Autocomplete
                      onSelect={(e) => setNewGroup(e.key)}
                      data={[...showMove.groups, { key: "-1", label: t("modals.icd10AddCode.createNewGroup") }]}
                    />
                  </div>
                  {newGroup === "-1" && (
                    <div className="row-form vertical-align">
                      <label>{t("modals.icd10AddCode.groupName")}</label>
                      <input
                        type="text"
                        placeholder={t("modals.icd10AddCode.groupName")}
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                      ></input>
                    </div>
                  )}
                </div>

                <div className="modal-actions center">
                  <button
                    className="dx-primary"
                    onClick={(e) => {
                      createRipple(e);
                      moveToGroup();
                    }}
                  >
                    {t("modals.icd10AddCode.save")}
                  </button>
                </div>
              </Modal>
            )}
          </div>
          <div>
            <div>
              <h3>{t("modals.icd10AddCode.favorites")}</h3>
              {Object.keys(favourites)?.length > 0 ? (
                <div>
                  {Object.keys(favourites)?.map((group) => (
                    <div key={group} className="icd-10-favourites">
                      {group !== "default" && favourites[group].length > 0 && <h4>{group}</h4>}
                      <div className="icd-10-list">
                        {favourites[group]?.map((icd10, index) => (
                          <div className="icd-10-item" key={"code_fav_" + index}>
                            <div className="icd-10-code">
                              <div className="badge gray">{icd10.code}</div>
                            </div>
                            <div className="icd-10-desc">{icd10.description}</div>
                            <div className="icd-10-action">
                              <button
                                className="small white no-border round"
                                onClick={() =>
                                  setShowMove({
                                    group,
                                    index,
                                    icd10,
                                    groups: Object.keys(favourites).map((g) => {
                                      return { key: g, label: g };
                                    }),
                                  })
                                }
                                data-tooltip-id="my-tooltip-icd"
                                data-tooltip-content={t("modals.icd10AddCode.tooltip.moveGroup")}
                              >
                                <i className="notranslate material-icons">swap_horiz</i>
                              </button>
                              <button
                                className="small white no-border round"
                                onClick={() =>
                                  addCode({
                                    n: icd10.code,
                                    d: icd10.description,
                                  })
                                }
                                data-tooltip-id="my-tooltip-icd"
                                data-tooltip-content={t("modals.icd10AddCode.tooltip.addCode")}
                              >
                                <i className="notranslate material-icons">add</i>
                              </button>
                              <button
                                className="small white no-border round"
                                onClick={() => removeCodeFavourites(group, index)}
                                data-tooltip-id="my-tooltip-icd"
                                data-tooltip-content={t("modals.icd10AddCode.tooltip.removeFavorite")}
                              >
                                <i className="notranslate material-icons">delete</i>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="empty">{t("modals.icd10AddCode.noFavorites")}</p>
              )}
            </div>
          </div>
        </div>

        <div className="modal-actions center dx-edit-footer">
          <button
            className="dx-primary"
            onClick={(e) => {
              createRipple(e);
              save();
            }}
          >
            {t("modals.icd10AddCode.save")}
          </button>
        </div>
      </Modal>
    </ConditionalDndProvider>
  );
}

export default ModalICD10AddCode;
